import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {HeroV3SlicePricingPrimaryProductsItem} from 'prismicio-types'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {UnderlinedTooltip} from 'slices/ProductSelectorSection/components/UnderlinedTooltip'
import {ButtonV2} from 'src/common/ButtonV2'
import {Icon} from 'src/common/Icon'

export const PricingCard = ({
  product,
  index,
  context,
  componentIndex,
}: {
  product: HeroV3SlicePricingPrimaryProductsItem
  index: number
  context: {pageUid: string}
  componentIndex: number
}) => {
  const {linkResolver} = useLinkResolver()

  return (
    <div
      key={`product-${index}`}
      className={cn('relative flex w-full flex-col justify-between gap-6 rounded-lg p-4 md:gap-8 md:p-6', {
        'bg-moss-white/[0.1]': index === 0,
        'bg-[rgba(185,255,63,0.10)]': index !== 0,
      })}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <h3 className="text-24 font-medium leading-[normal] text-moss-white">{product.name}</h3>
          {isFilled.keyText(product.tag) && (
            <div className="relative h-min rounded-[4px] bg-[#E2FFAF] px-2 py-1">
              <p className="text-tg-body-s text-moss-black">{product.tag}</p>
              <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-moss-black/[.1]" />
            </div>
          )}
        </div>
        <PrismicRichText
          field={product.body}
          components={{
            list: ({children}) => <ul className="flex flex-col gap-2">{children}</ul>,
            listItem: ({children}) => (
              <li className="text-tg-body-m flex h-min items-start gap-1.5 tracking-[0.15px] text-moss-white">
                <div className="relative isolate">
                  <Icon name="filled-check-v2" className="mt-0.5 size-4 min-w-4 text-green-default" />
                  <div className="absolute left-1/2 top-1/2 -z-10 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-moss-white" />
                </div>
                {children}
              </li>
            ),
            paragraph: ({children}) => (
              <p className="text-tg-body-m pb-1 tracking-[0.15px] text-moss-white/[0.7] md:pb-3">{children}</p>
            ),
            hyperlink: ({children}) => (
              <UnderlinedTooltip
                id={product.name!.replace(/\s/g, '-').toLowerCase()}
                place="right"
                pageName="ProductSelectorSection"
                content={product.tooltip_content}
              >
                {children}
              </UnderlinedTooltip>
            ),
          }}
        />
      </div>
      <ButtonV2
        trackingData={{
          location: `component-${componentIndex}-hero-v3`,
          dataType: `primary-button`,
          pageName: context.pageUid,
          mktgId: `product-${product.name?.toLowerCase()}`,
        }}
        className="!h-[38px] w-min"
        variant="primary"
        href={linkResolver(product.button_link)}
      >
        {product.button_link.text}
      </ButtonV2>
      {index === 0 && (
        <div className="pointer-events-none absolute inset-0 rounded-lg border border-moss-white/[0.1]" />
      )}
    </div>
  )
}
